import { useEffect, useState } from 'react';
import { getCurrentNZMonthString } from '../../utils/nzDateUtils';
import { computeForecastCategoryAmounts } from '../../utils/analysisForecast';
import { computeProjectedMonthAmounts, makeProjectedMonth } from '../../utils/projectedMonth';

/**
 * Custom hook to generate forecast months and initial forecast data from budgetData.
 * Returns [forecastMonths, forecastData]
 *
 * A synthetic projected column for the live month (utils/projectedMonth) always
 * leads the forecast list, so it surfaces whenever forecast columns are shown.
 * It is a forecast month for every purpose — the table reads it through the
 * same accessors, and the balance chain treats it as the first forecast month —
 * so the months after it rebase onto the live month's projected landing rather
 * than its part-way actuals.
 */
export default function useForecastData(budgetData, {
  showForecast,
  budgets,
  categories,
  transactions = [],
  actions = [],
}) {
  const [forecastMonths, setForecastMonths] = useState([]);
  const [forecastData, setForecastData] = useState({
    incomeCategories: {},
    expenseCategories: {},
    monthlySummary: {}
  });

  // Generate forecast months
  useEffect(() => {
    // Use current NZ month to determine where forecasting should start
    const currentNZMonth = getCurrentNZMonthString();
    const [currentMonthAbbr, currentYearStr] = currentNZMonth.split('-');
    const currentMonthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(currentMonthAbbr);
    const currentYear = 2000 + parseInt(currentYearStr, 10);

    const forecastMonthsList = [];
    const forecastIncome = {};
    const forecastExpenses = {};
    const forecastSummary = {};

    // The live month's projected landing leads the list, so the calendar
    // forecast months that follow chain off it.
    if (budgetData.months.includes(currentNZMonth)) {
      const projectedMonth = makeProjectedMonth(currentNZMonth);
      forecastMonthsList.push(projectedMonth);
      forecastSummary[projectedMonth] = { income: 0, expenses: 0 };
    }

    // Generate forecast months starting from NEXT month (never include current month as forecast)
    for (let i = 1; i <= 12; i++) {
      let newMonthIndex = (currentMonthIndex + i) % 12;
      let newYear = currentYear + Math.floor((currentMonthIndex + i) / 12);
      const newMonthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][newMonthIndex];
      const newMonth = `${newMonthAbbr}-${newYear.toString().substr(2, 2)}`;

      forecastMonthsList.push(newMonth);
      forecastSummary[newMonth] = { income: 0, expenses: 0 };
    }

    // Initialize forecast categories with the same categories as actual data
    Object.keys(budgetData.incomeCategories).forEach(category => {
      forecastIncome[category] = {};
      forecastMonthsList.forEach(month => {
        forecastIncome[category][month] = 0;
      });
    });

    Object.keys(budgetData.expenseCategories).forEach(category => {
      forecastExpenses[category] = {};
      forecastMonthsList.forEach(month => {
        forecastExpenses[category][month] = 0;
      });
    });

    setForecastMonths(forecastMonthsList);
    setForecastData({
      incomeCategories: forecastIncome,
      expenseCategories: forecastExpenses,
      monthlySummary: forecastSummary
    });
  }, [budgetData]);

  // Calculate forecast data based on budgets — the shared Analysis forecast
  // math, so other surfaces (Accounts page cards) can reproduce these figures.
  // The projected current month is excluded: it is not budget-driven but
  // landing-driven, and gets its amounts from utils/projectedMonth below.
  useEffect(() => {
    if (!showForecast || forecastMonths.length === 0) return;

    const currentNZMonth = getCurrentNZMonthString();
    const projectedMonth = forecastMonths.find(m => m === makeProjectedMonth(currentNZMonth));
    const calendarMonths = forecastMonths.filter(m => m !== projectedMonth);

    const next = computeForecastCategoryAmounts({
      forecastMonths: calendarMonths,
      budgets,
      categories,
      budgetData,
    });

    if (projectedMonth) {
      const projected = computeProjectedMonthAmounts({
        month: projectedMonth,
        categories,
        transactions,
        actions,
        budgetData,
      });
      Object.entries(projected.incomeCategories).forEach(([categoryId, amount]) => {
        if (!next.incomeCategories[categoryId]) next.incomeCategories[categoryId] = {};
        next.incomeCategories[categoryId][projectedMonth] = amount;
      });
      Object.entries(projected.expenseCategories).forEach(([categoryId, amount]) => {
        if (!next.expenseCategories[categoryId]) next.expenseCategories[categoryId] = {};
        next.expenseCategories[categoryId][projectedMonth] = amount;
      });
      next.monthlySummary[projectedMonth] = {
        income: projected.summary.income,
        expenses: projected.summary.expenses,
      };
    }

    setForecastData(next);
  }, [showForecast, forecastMonths, budgets, categories, budgetData, transactions, actions]);


  return [forecastMonths, forecastData];
}
